import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { bindActionCreators } from "redux";
import { DarkMode, LoadingStatus } from "../../model/enum/enums";
import { User } from '../../model/User';
import { userActions } from '../../store/slices/userSlice';
import { AppDispatch, RootState } from '../../store/store';
import { GetRazorSiteUrl } from "../../utils/URLUtils";
import useFetch from "../api/useFetch";
const useUser = (autoFetch: boolean = true) => {
    const dispatch = useDispatch<AppDispatch>();
    const { send } = useFetch<any>();
    const ac = bindActionCreators(userActions, dispatch);
    const { userInfo, isUserLoaded, status, error } = useSelector((state: RootState) => state.user);
    const navigate = useNavigate();

    useEffect(() => {
        if (!userInfo && autoFetch)
            ac.getUser();
    }, [ac, userInfo, autoFetch]);

    const setUserInfo = useCallback((info: User | null) => {
        ac.setUser(info);
    }, [ac]);



    const updateUserInfo = useCallback((info: Partial<User>) => {
        ac.updateUser(info);
    }, [ac]);

    const handleLogout = useCallback(() => {
        ac.logout();
    }, [ac]);

    const isAuthenticated = useCallback(() => {
        return !!userInfo?.Token;
    }, [userInfo?.Token]);

    const isNonEnglishNativeLanguage = useCallback(() => {
        if (!userInfo?.NativeLanguageCode) return false;
        return userInfo.NativeLanguageCode !== 'en';
    }, [userInfo?.NativeLanguageCode]);

    const fetchUserData = useCallback(async (token?: string): Promise<User | null> => {
        const { response } = await send(GetRazorSiteUrl('settings', token ?? userInfo?.Token));
        if (!response) {
            setUserInfo(null);
            navigate('/logout');
            return null;
        }
        const user = { ...response, Token: token ?? userInfo?.Token };
        setUserInfo(user);
        return user;
    }, [userInfo?.Token, send, setUserInfo, navigate]);

    const isWordUpUser = useCallback(() => {
        return userInfo?.Email.endsWith('@wordupapp.co');
    }, [userInfo?.Email]);

    const isProUser = useCallback(() => {
        return userInfo?.Pro && userInfo?.ForceToBasic;
    }, [userInfo?.Pro, userInfo?.ForceToBasic]);

    const isDarkMode = useCallback(() => {
        return userInfo?.DarkMode === DarkMode.Dark;
    }, [userInfo?.DarkMode]);

    const ensureCasing = useCallback((text: string) => {
        switch (userInfo?.Casing) {
            case "lower":
                return text.toLowerCase();
            case "UPPER":
                return text.toUpperCase();
            default:
                return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
        };
    }, [userInfo?.Casing]);

    return {
        userInfo,
        setUserInfo,
        updateUserInfo,
        handleLogout,
        isAuthenticated,
        isUserLoaded,
        isNonEnglishNativeLanguage,
        fetchUserData,
        isLoading: status === LoadingStatus.LOADING,
        isError: status === LoadingStatus.FAILED,
        error,
        isWordUpUser,
        isProUser,
        isDarkMode,
        ensureCasing,
    }
};

export default useUser;